import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  User,
  Calendar,
  FileText,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Activity,
  Stethoscope,
  Pill,
  Camera
} from 'lucide-react';

interface VetAxisDemoProps {
  isActive: boolean;
}

const VetAxisDemo: React.FC<VetAxisDemoProps> = ({ isActive }) => {
  const [currentView, setCurrentView] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);

  const views = [
    { name: 'dashboard', title: 'Dashboard Overview', icon: Activity },
    { name: 'client', title: 'Client Profile', icon: User },
    { name: 'animal', title: 'Animal Records', icon: Heart },
    { name: 'appointment', title: 'Appointment Scheduling', icon: Calendar },
    { name: 'invoice', title: 'Billing & Invoices', icon: DollarSign }
  ];

  const clients = [
    {
      id: 1,
      name: 'Sarah Johnson',
      phone: '(555) 123-4567',
      email: 'sarah.johnson@email.com',
      address: '123 Oak Street, Springfield',
      animals: ['Bella (Dog)', 'Whiskers (Cat)'],
      lastVisit: '2024-01-15',
      status: 'active'
    },
    {
      id: 2,
      name: 'Mike Rodriguez',
      phone: '(555) 987-6543',
      email: 'mike.rodriguez@email.com',
      address: '456 Pine Avenue, Springfield',
      animals: ['Rex (Dog)'],
      lastVisit: '2024-01-20',
      status: 'active'
    }
  ];

  const animals = [
    {
      id: 1,
      name: 'Bella',
      species: 'Canine',
      breed: 'Golden Retriever',
      age: '3 years',
      weight: '28 kg',
      owner: 'Sarah Johnson',
      microchip: '982000123456789',
      lastVisit: '2024-01-15',
      status: 'healthy'
    },
    {
      id: 2,
      name: 'Whiskers',
      species: 'Feline',
      breed: 'Persian',
      age: '5 years',
      weight: '4.5 kg',
      owner: 'Sarah Johnson',
      microchip: '982000987654321',
      lastVisit: '2024-01-10',
      status: 'monitoring'
    }
  ];

  const appointments = [
    { time: '9:00 AM', client: 'Sarah Johnson', animal: 'Bella', type: 'Routine Checkup', status: 'confirmed' },
    { time: '10:30 AM', client: 'Mike Rodriguez', animal: 'Rex', type: 'Vaccination', status: 'completed' },
    { time: '2:00 PM', client: 'Lisa Chen', animal: 'Fluffy', type: 'Surgery Consultation', status: 'upcoming' }
  ];

  useEffect(() => {
    if (isActive) {
      const viewInterval = setInterval(() => {
        setCurrentView(prev => (prev + 1) % views.length);
        setAnimationStep(0);
      }, 4000);

      return () => clearInterval(viewInterval);
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      const stepInterval = setInterval(() => {
        setAnimationStep(prev => (prev + 1) % 3);
      }, 1500);

      return () => clearInterval(stepInterval);
    }
  }, [isActive, currentView]);

  const renderDashboard = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <motion.div
          className="glass-effect p-4 rounded-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Today's Appointments</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">12</div>
        </motion.div>

        <motion.div
          className="glass-effect p-4 rounded-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Animals in Care</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">847</div>
        </motion.div>
      </div>

      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Recent Activity</h4>
        <div className="space-y-2">
          {[
            { icon: CheckCircle, text: 'Bella completed vaccination', time: '10 min ago', color: 'text-green-500' },
            { icon: FileText, text: 'New client registration: Mike Rodriguez', time: '25 min ago', color: 'text-blue-500' },
            { icon: Calendar, text: 'Surgery scheduled for Whiskers', time: '1 hour ago', color: 'text-purple-500' }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-2 text-xs"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.2 }}
            >
              <item.icon className={`w-3 h-3 ${item.color}`} />
              <span className="text-slate-700 dark:text-gray-300 flex-1">{item.text}</span>
              <span className="text-slate-500 dark:text-gray-500">{item.time}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderClientProfile = () => (
    <div className="space-y-4">
      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white">Sarah Johnson</h4>
            <p className="text-sm text-slate-600 dark:text-gray-400">Client ID: #CLT001</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-500" />
            <span className="text-slate-700 dark:text-gray-300">(555) 123-4567</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-500" />
            <span className="text-slate-700 dark:text-gray-300">sarah.johnson@email.com</span>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            <span className="text-slate-700 dark:text-gray-300">123 Oak Street, Springfield</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Animals</h4>
        <div className="space-y-2">
          {['Bella (Golden Retriever)', 'Whiskers (Persian Cat)'].map((animal, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-2 p-2 bg-white/5 rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + index * 0.2 }}
            >
              <Heart className="w-4 h-4 text-red-500" />
              <span className="text-sm text-slate-700 dark:text-gray-300">{animal}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderAnimalRecords = () => (
    <div className="space-y-4">
      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white">Bella</h4>
            <p className="text-sm text-slate-600 dark:text-gray-400">Golden Retriever • 3 years old</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <span className="text-slate-500 dark:text-gray-500">Weight:</span>
            <span className="text-slate-700 dark:text-gray-300 ml-2">28 kg</span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-gray-500">Microchip:</span>
            <span className="text-slate-700 dark:text-gray-300 ml-2">982000123456789</span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-gray-500">Owner:</span>
            <span className="text-slate-700 dark:text-gray-300 ml-2">Sarah Johnson</span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-gray-500">Status:</span>
            <span className="text-green-600 dark:text-green-400 ml-2">Healthy</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Recent Visits</h4>
        <div className="space-y-3">
          {[
            { date: 'Jan 15, 2024', type: 'Annual Checkup', veterinarian: 'Dr. Smith', notes: 'Healthy, vaccinations up to date' },
            { date: 'Dec 10, 2023', type: 'Vaccination', veterinarian: 'Dr. Johnson', notes: 'Rabies and DHPP vaccines administered' }
          ].map((visit, index) => (
            <motion.div
              key={index}
              className="bg-white/5 rounded-lg p-3"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + index * 0.2 }}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-primary-500" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{visit.type}</span>
                </div>
                <span className="text-xs text-slate-500 dark:text-gray-500">{visit.date}</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-gray-400 mb-1">Dr: {visit.veterinarian}</p>
              <p className="text-xs text-slate-700 dark:text-gray-300">{visit.notes}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderAppointmentScheduling = () => (
    <div className="space-y-4">
      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-slate-900 dark:text-white">Today's Schedule</h4>
          <div className="text-sm text-slate-600 dark:text-gray-400">January 25, 2024</div>
        </div>

        <div className="space-y-3">
          {appointments.map((appointment, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.2 }}
            >
              <div className="text-center min-w-[60px]">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{appointment.time}</div>
              </div>

              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{appointment.client}</div>
                <div className="text-xs text-slate-600 dark:text-gray-400">{appointment.animal} • {appointment.type}</div>
              </div>

              <div className="flex items-center gap-1">
                {appointment.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                {appointment.status === 'confirmed' && <Clock className="w-4 h-4 text-blue-500" />}
                {appointment.status === 'upcoming' && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                <span className={`text-xs font-medium ${
                  appointment.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                  appointment.status === 'confirmed' ? 'text-blue-600 dark:text-blue-400' :
                  'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderInvoicing = () => (
    <div className="space-y-4">
      <motion.div
        className="glass-effect p-4 rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-slate-900 dark:text-white">Invoice #INV-2024-001</h4>
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded-full">Paid</span>
        </div>

        <div className="text-sm mb-4">
          <div className="text-slate-600 dark:text-gray-400 mb-2">Client: Sarah Johnson</div>
          <div className="text-slate-600 dark:text-gray-400">Date: January 15, 2024</div>
        </div>

        <div className="space-y-2">
          {[
            { service: 'Annual Health Checkup - Bella', price: '$75.00' },
            { service: 'Vaccination (Rabies)', price: '$25.00' },
            { service: 'Blood Work Panel', price: '$45.00' }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex justify-between items-center p-2 bg-white/5 rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <span className="text-sm text-slate-700 dark:text-gray-300">{item.service}</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{item.price}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="border-t border-white/10 pt-3 mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-slate-900 dark:text-white">Total:</span>
            <span className="text-lg font-bold text-primary-500">$145.00</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case 0: return renderDashboard();
      case 1: return renderClientProfile();
      case 2: return renderAnimalRecords();
      case 3: return renderAppointmentScheduling();
      case 4: return renderInvoicing();
      default: return renderDashboard();
    }
  };

  if (!isActive) {
    return (
      <div className="w-full max-w-md mx-auto glass-effect rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">VetAxis Demo</h3>
        <p className="text-slate-600 dark:text-gray-400">Click "Learn More" to explore the veterinary practice management interface</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      <motion.div
        className="glass-effect rounded-2xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">VetAxis</h3>
              <p className="text-xs text-slate-600 dark:text-gray-400">Practice Management</p>
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              {views[currentView].title}
            </div>
            <div className="text-xs text-slate-600 dark:text-gray-400">
              {currentView + 1} of {views.length}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-white/10">
          {views.map((view, index) => (
            <div
              key={view.name}
              className={`flex-1 p-2 text-center transition-colors ${
                index === currentView
                  ? 'bg-primary-500/20 text-primary-500'
                  : 'text-slate-600 dark:text-gray-400'
              }`}
            >
              <view.icon className="w-4 h-4 mx-auto" />
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className="p-6 min-h-[400px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderCurrentView()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default VetAxisDemo;